import { useDisclosure } from '@chakra-ui/react'
import {
  BadgeIndicator,
  Button,
  CheckBoxGroup,
  Dialog,
  Divider,
  Flex,
  FunnelIcon,
  IconButton,
  Menu,
  PlusIcon,
  Spinner,
  Text,
  Tooltip,
  XMarkIcon,
} from 'components'
import { useWorkspace } from 'components/providers'
import { isEqual, startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { AggregationRequest, DCFilter, IEvent } from 'types'
import { getAggregations } from 'utils'
import {
  excludeFilters,
  getContextFiltersForAggregation,
  getFilterContext,
  getFilterKey,
  getFiltersFromConditions,
  getOtgMemberSessionFilters,
  useEvent,
  useOnTheGo,
} from '../utils'
import { LeadTableColumn } from '../utils/leadSessionUtils'
import FilterTabs from './FilterTabs'

interface Props {
  columns: LeadTableColumn[]
}

interface FilterValueRow {
  count: number
  value: string | number | null
  selected?: boolean
}

export default function FilterSelector({ columns }: Props) {
  const { event, filters: eventFilters = [], setFilters: setEventFilters } = useEvent()
  const {
    filters: onTheGoFilters = [],
    memberId: onTheGoMemberId,
    setFilters: setOnTheGoFilters,
  } = useOnTheGo()
  const { workspace } = useWorkspace()
  const filters = event ? eventFilters : onTheGoFilters
  const setFilters = event ? setEventFilters : setOnTheGoFilters
  const [draft, setDraft] = useState(filters)
  const [loading, setLoading] = useState(false)
  const { open, onOpen, onClose } = useDisclosure()
  const [selectedFilter, setSelectedFilter] = useState<DCFilter>()
  const [filterValues, setFilterValues] = useState<FilterValueRow[]>([])
  const appliedFiltersCounts = getFiltersFromConditions(filters, columns).length
  const draftFilters = getFiltersFromConditions(draft, columns)
  const draftFiltersCount = draftFilters.length || 0
  const dirty = !isEqual(filters, draft)

  const menuOptions = columns
    .filter(
      column =>
        !draft.some(filter => getFilterKey(filter, columns) === column.key) &&
        column.source !== 'custom' &&
        !excludeFilters.includes(column.key)
    )
    .sort((a, b) => a.key.localeCompare(b.key))

  function handleOpen() {
    setDraft(filters)
    const firstFilter = getFiltersFromConditions(filters, columns)[0]
    setSelectedFilter(firstFilter ?? undefined)
    onOpen()
  }

  function handleClose() {
    setDraft(filters)
    setSelectedFilter(undefined)
    onClose()
  }

  function handleDone() {
    setFilters(draft)
    handleClose()
  }

  function handleClear() {
    setFilters([])
    handleClose()
  }

  function addNewFilter(filter: string) {
    let newFilter: DCFilter = {
      field: filter,
      table: 'leadSessions',
      operator: 'eq',
      value: '',
    }
    const newFilterContext = getFilterContext(newFilter, columns)
    newFilter.field = newFilterContext.field
    newFilter.table = newFilterContext.parentTable
    if (newFilterContext.secondaryTargetField) {
      newFilter.secondaryTargetField = newFilterContext.secondaryTargetField
      newFilter.secondaryTargetValue = newFilterContext.value
    }
    setDraft(prev => [...prev, newFilter])
    setSelectedFilter(newFilter)
  }

  function removeFilter(filter: DCFilter) {
    const removedKey = getFilterKey(filter, columns)
    const newFilters = draft.filter(f => getFilterKey(f, columns) !== removedKey)
    setDraft(newFilters)
    if (selectedFilter && getFilterKey(selectedFilter, columns) === removedKey) {
      const remaining = getFiltersFromConditions(newFilters, columns)
      setSelectedFilter(remaining[0])
    }
  }

  function updateDraft(selected: DCFilter, values: string[]) {
    const selectedKey = getFilterKey(selected, columns)
    const firstIndex = draft.findIndex(f => getFilterKey(f, columns) === selectedKey)
    const otherFilters = draft.filter(filter => getFilterKey(filter, columns) !== selectedKey)
    const newFilterConditions: DCFilter[] = values.map(value => {
      const newFilterCondition: DCFilter = {
        field: selected.field,
        operator: value ? 'eq' : 'isNull',
        value: value ? value : true,
        table: selected.table,
      }
      if (selected.secondaryTargetField) {
        newFilterCondition.secondaryTargetField = selected.secondaryTargetField
        newFilterCondition.secondaryTargetValue = selected.secondaryTargetValue
      }
      const newFilterContext = getFilterContext(newFilterCondition, columns)
      newFilterCondition.field = newFilterContext.field
      newFilterCondition.table = newFilterContext.parentTable
      if (newFilterContext.secondaryTargetField) {
        newFilterCondition.secondaryTargetField = newFilterContext.secondaryTargetField
        newFilterCondition.secondaryTargetValue = newFilterContext.value
      }
      return newFilterCondition
    })
    const insertIndex = firstIndex >= 0 ? firstIndex : otherFilters.length
    const newFilters = [
      ...otherFilters.slice(0, insertIndex),
      ...newFilterConditions,
      ...otherFilters.slice(insertIndex),
    ]
    setDraft(newFilters)
  }

  async function getSessionValues(
    eventId: string | undefined,
    workspaceId: string,
    filter: DCFilter
  ) {
    setLoading(true)
    const filterContext = getFilterContext(filter, columns)
    const contextFilters = getContextFiltersForAggregation(draft, filter, columns)
    const filterBaseFilters = filterContext.secondaryTargetField
      ? [
          {
            field: filterContext.secondaryTargetField,
            operator: 'eq' as const,
            value: filterContext.value,
            table: filterContext.parentTable,
          },
        ]
      : []
    const onTheGoBaseFilters = onTheGoMemberId ? getOtgMemberSessionFilters(onTheGoMemberId) : []
    const aggregationReq: AggregationRequest[] = [
      {
        categories: [filterContext.field],
        filters: [...onTheGoBaseFilters, ...filterBaseFilters, ...contextFilters],
        table: filterContext.parentTable,
        values: ['_count', filterContext.field],
      },
    ]
    const filterValuesFromSessions = (
      await getAggregations({
        aggregations: aggregationReq,
        ...(eventId ? { eventIds: [eventId] } : {}),
        workspaceIds: [workspaceId],
      })
    ).data[0]

    if (filterValuesFromSessions?.length) {
      setFilterValues(
        filterValuesFromSessions.map((data: any) => {
          const value: string = data[filterContext.field]
          const isBlank = value == null || value === ''
          const selected = draft.some(f => {
            if (getFilterKey(f, columns) !== getFilterKey(filter, columns)) return false
            if (f.operator === 'isNull' && f.value === true) return isBlank
            if (isBlank) return (f.operator === 'isNull' && f.value === true) || f.value === null
            return f.value === value
          })
          return {
            count: data['_count'] as number,
            value,
            selected,
          }
        })
      )
    } else setFilterValues([])

    setLoading(false)
  }

  useEffect(() => {
    if (!open || !selectedFilter || !workspace) return
    getSessionValues(event?.id, workspace.id, selectedFilter)
  }, [event?.id, open, selectedFilter, workspace?.id])

  if (!workspace) return

  return (
    <>
      <BadgeIndicator value={appliedFiltersCounts} badgeProps={{ bg: `red.400`, fontSize: '9px' }}>
        <Tooltip content="Filters">
          <IconButton
            variant="solid"
            icon={<FunnelIcon type="outline" />}
            aria-label="filter-selector"
            onClick={handleOpen}
          />
        </Tooltip>
      </BadgeIndicator>
      <Dialog
        size="xl"
        title="Filters"
        placement="center"
        bodyProps={{
          gap: 4,
          display: 'flex',
          flexDirection: 'column',
          px: 4,
          overflow: 'hidden',
        }}
        open={open}
        onClose={handleClose}
        footer={
          <Flex flex={1} justify="space-between" align="center" gap={2}>
            <Text textStyle="bodyMedium" opacity={draftFiltersCount > 0 ? 1 : 0}>
              Filters: {draftFiltersCount}
            </Text>

            <Flex justify="flex-end" gap={2}>
              <Button
                variant="outline"
                disabled={!draftFilters.length}
                leftIcon={<XMarkIcon fontSize="sm" color="red.500" />}
                onClick={handleClear}
              >
                Clear
              </Button>
              <Button palette="primary" disabled={!dirty} onClick={handleDone}>
                Apply
              </Button>
            </Flex>
          </Flex>
        }
      >
        <Flex gap={2} h="50vh">
          <Flex direction="column" gap={1} pr={3}>
            <Menu
              menuProps={{
                matchWidth: true,
                closeOnSelect: true,
              }}
              buttonLabel={<Text textStyle="bodySmall">Add Filter</Text>}
              options={menuOptions.map(option => ({
                value: option.key,
                label: option.title,
              }))}
              onChange={addNewFilter}
              hideCheckbox
              rightIcon={undefined}
              leftIcon={<PlusIcon />}
            />
            {selectedFilter && (
              <FilterTabs
                columns={columns}
                filters={draft}
                onRemove={removeFilter}
                selectedFilter={selectedFilter}
                onClick={setSelectedFilter}
              />
            )}
          </Flex>
          <Divider key={draft.length} orientation="vertical" />
          <Flex w="100%" overflowY="auto" align="center" justify="center">
            {loading ? (
              <Spinner />
            ) : selectedFilter ? (
              filterValues.length ? (
                <Flex flex={1} h="100%">
                  <CheckBoxGroup
                    onChange={values => updateDraft(selectedFilter, values)}
                    values={filterValues
                      .map(({ value, count, selected }) => ({
                        checked: selected || false,
                        value: value?.toString() || '',
                        label: (
                          <Flex gap={1}>
                            <Text
                              opacity={value != null ? 1 : 0.5}
                              fontStyle={value != null ? 'normal' : 'italic'}
                            >
                              {filterValueRenderer(selectedFilter, value, event)}
                            </Text>
                            <Text opacity={0.5} fontStyle="italic">
                              ({count})
                            </Text>
                          </Flex>
                        ),
                      }))
                      .sort((a, b) => a.value.localeCompare(b.value))}
                  />
                </Flex>
              ) : (
                <Text textStyle="bodyMedium" opacity={0.5}>
                  No values available based on added filters
                </Text>
              )
            ) : (
              <Text textStyle="bodyMedium" opacity={0.5}>
                Add a filter to see values available
              </Text>
            )}
          </Flex>
        </Flex>
        <Divider />
      </Dialog>
    </>
  )
}

function filterValueRenderer(
  selectedFilter: DCFilter,
  value: string | number | null,
  event?: IEvent | null
): string {
  const { field } = selectedFilter
  if (value === null) return 'Blank'

  switch (field) {
    case 'momentId': {
      if (!event) return startCase(value?.toString())
      const momentTitle = event.moments.find(m => m.id === value)?.title
      return momentTitle || 'Blanks'
    }

    default:
      return startCase(value?.toString())
  }
}
